/**
 * Télémétrie d'erreurs client (anti-page-blanche, audit M1).
 *
 * - reportClientError : POST fire-and-forget vers /api/client-log, JAMAIS
 *   bloquant, dédup (même message ≤ 1×/session via sessionStorage).
 * - installGlobalErrorHandlers : window.onerror (erreurs JS) + onunhandledrejection.
 * - installAssetErrorNet (VOLET 3) : capture STRICTE des échecs de chargement
 *   <script>/<link> du bundle → reload one-shot gardé sessionStorage
 *   ('asset_reload_done'). JAMAIS de reload sur crash de rendu (boucle interdite).
 */
import { API_URL } from './apiConfig';

const SENT_KEY = 'client_telemetry_sent';
const ASSET_RELOAD_KEY = 'asset_reload_done';

function _sentSet() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SENT_KEY) || '[]'));
  } catch (_) {
    return new Set();
  }
}

function _markSent(key) {
  try {
    const s = _sentSet();
    s.add(key);
    // borne la taille du dédup (garde les 50 derniers).
    sessionStorage.setItem(SENT_KEY, JSON.stringify([...s].slice(-50)));
  } catch (_) { /* ignore */ }
}

/**
 * Envoie une erreur client au backend. Fire-and-forget : ne throw jamais, ne
 * bloque jamais l'UI. Dédup par message (≤ 1× par session).
 */
export function reportClientError({ message, stack, route } = {}) {
  try {
    const msg = String(message || '').slice(0, 512);
    if (!msg) return;
    if (_sentSet().has(msg)) return; // dédup : même message ≤ 1×/session
    _markSent(msg);
    const body = JSON.stringify({
      message: msg,
      stack: String(stack || '').slice(0, 4096),
      route: (typeof window !== 'undefined' && window.location)
        ? window.location.pathname : (route || ''),
      ua: (typeof navigator !== 'undefined') ? navigator.userAgent : '',
      ts: new Date().toISOString(),
    });
    // keepalive : survit à un reload/navigation imminent ; catch silencieux.
    fetch(`${API_URL}/api/client-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch (_) { /* jamais bloquer l'UI */ }
}

/** Handlers globaux pour les erreurs JS hors rendu React. */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    // Uniquement les vraies erreurs JS (event.error présent). Les échecs de
    // chargement de ressource (target = élément) sont gérés par le filet asset.
    if (event && event.error) {
      reportClientError({
        message: event.message || String(event.error),
        stack: event.error && event.error.stack,
      });
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    const r = event && event.reason;
    reportClientError({
      message: (r && r.message) || String(r),
      stack: r && r.stack,
    });
  });
}

/**
 * VOLET 3 — filet asset. Capture STRICTE (capture=true : les erreurs de
 * chargement de ressource ne bubblent pas) filtrée sur <script>/<link> uniquement.
 * Reload one-shot gardé → si le reload ne résout pas, on ne boucle pas.
 */
export function installAssetErrorNet() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    const el = event && event.target;
    if (!el || el === window) return; // erreur JS, pas un asset → ignoré ici
    const tag = (el.tagName || '').toUpperCase();
    if (tag !== 'SCRIPT' && tag !== 'LINK') return; // STRICTEMENT ressource
    const src = el.src || el.href || '';
    reportClientError({ message: `asset_load_failed: ${tag} ${src}`.slice(0, 512) });
    try {
      if (sessionStorage.getItem(ASSET_RELOAD_KEY) !== '1') {
        sessionStorage.setItem(ASSET_RELOAD_KEY, '1'); // one-shot
        window.location.reload();
      }
    } catch (_) { /* ignore */ }
  }, true);
}
